// src/admin/components/whatsapp/WhatsAppSettings.jsx
import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import {
    RiWhatsappLine,
    RiSaveLine,
    RiSendPlaneLine,
    RiNotificationLine,
    RiUserFollowLine,
    RiBuildingLine,
    RiBankCard2Line,
    RiLoader4Line,
    RiCheckLine,
    RiCloseLine,
    RiSettings4Line,
} from 'react-icons/ri';
import api from '../../../services/api';


export default function WhatsAppSettings() {
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingReminders, setSendingReminders] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewList, setPreviewList] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [toast, setToast] = useState(null);
    const [defaultCountry, setDefaultCountry] = useState('iq'); // Fallback location

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    useEffect(() => {
        // Fetch location for default country code
        fetch('https://ipapi.co/json/')
            .then(res => res.json())
            .then(data => {
                if (data.country_code) setDefaultCountry(data.country_code.toLowerCase());
            })
            .catch(() => console.log('Location detection failed'));

        api.get('/whatsapp/settings')
            .then(r => {
                if (r.data.success) setSettings(r.data.settings || {});
            })
            .catch(() => showToast(t('failed_to_load_settings') || 'Failed to load settings', 'error'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const r = await api.put('/whatsapp/settings', settings);
            if (r.data.success) {
                setSettings(r.data.settings);
                showToast(t('settings_saved') || 'Settings saved successfully');
            } else {
                showToast(r.data.message || t('failed_to_save') || 'Failed to save', 'error');
            }
        } catch (e) {
            showToast(e?.response?.data?.message || t('server_error') || 'Server error', 'error');
        }
        setSaving(false);
    };

    const handleSendReminders = async () => {
        setLoadingPreview(true);
        try {
            const r = await api.get('/whatsapp/preview-reminders');
            if (r.data.success) {
                setPreviewList(r.data.previewList || []);
                setShowConfirmModal(true);
            } else {
                showToast(r.data.message || 'Failed to generate preview', 'error');
            }
        } catch (e) {
            showToast(e?.response?.data?.message || 'Server error while generating preview', 'error');
        }
        setLoadingPreview(false);
    };

    const executeSendReminders = async () => {
        setShowConfirmModal(false);
        setSendingReminders(true);
        try {
            const r = await api.post('/whatsapp/send-reminders');
            if (r.data.success) {
                showToast(r.data.message || 'Reminders processed successfully', 'success');
            } else {
                showToast(r.data.message || 'Failed to send reminders', 'error');
            }
        } catch (e) {
            showToast(e?.response?.data?.message || 'Server error while sending reminders', 'error');
        }
        setSendingReminders(false);
    };


    const upd = (key, val) => setSettings(s => ({ ...s, [key]: val }));

    // ── Reusable UI pieces ──────────────────────────────────────────────────
    const card = `bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm mb-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : ''}`;
    const labelCls = `block text-sm font-semibold text-zinc-700 mb-2 ${isDarkMode ? 'text-zinc-300' : ''}`;
    const inputCls = `w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all ${isDarkMode
        ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500'
        : 'bg-white text-zinc-900 placeholder-zinc-400'}`;
    const textareaCls = `${inputCls} min-h-[120px] resize-y leading-relaxed`;
    const headingCls = `text-lg font-bold text-zinc-900 ${isDarkMode ? 'text-white' : ''}`;
    const subCls = `text-sm text-zinc-500 font-medium ${isDarkMode ? 'text-zinc-400' : ''}`;

    const Spinner = () => (
        <RiLoader4Line className="animate-spin" size={16} />
    );

    const Toggle = ({ checked, onChange, label, sub }) => (
        <label className="flex items-start gap-3 cursor-pointer mb-3">
            <input
                type="checkbox"
                className="sr-only"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <div
                style={{
                    flexShrink: 0,
                    marginTop: 2,
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    position: 'relative',
                    transition: 'background 0.2s',
                    background: checked ? '#dc2626' : isDarkMode ? '#52525b' : '#d1d5db',
                }}
            >
                <span style={{
                    position: 'absolute',
                    top: 2,
                    ...(isRTL ? { right: checked ? 22 : 2 } : { left: checked ? 22 : 2 }),
                    width: 20,
                    height: 20,
                    borderRadius: 9999,
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    transition: 'all 0.2s',
                    display: 'block',
                }} />
            </div>
            <div>
                <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{label}</div>
                {sub && <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{sub}</div>}
            </div>
        </label>
    );

    const SectionHeader = ({ icon: Icon, title }) => (
        <div className="flex items-center gap-2.5 mb-4">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-red-50'}`}>
                <Icon size={17} className="text-red-500" />
            </div>
            <h3 className={headingCls}>{title}</h3>
        </div>
    );



    // ── Loading ─────────────────────────────────────────────────────────────
    if (loading) return (
        <div className="flex items-center justify-center p-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-t-2 border-red-500" />
        </div>
    );

    if (!settings) return (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'text-red-400 bg-red-900/10 border-red-800' : 'text-red-700 bg-red-50 border-red-200'}`}>
            {t('failed_to_load_settings') || 'Failed to load settings'}
        </div>
    );

    return (
        <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-white text-sm font-medium transition-all
                    ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
                    {toast.type === 'success' ? <RiCheckLine size={18} /> : <RiCloseLine size={18} />}
                    {toast.message}
                </div>
            )}

            {/* Page Header */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-2xl bg-red-50 text-red-600">
                        <RiWhatsappLine size={32} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
                            {t('whatsapp_settings')}
                        </h1>
                        <p className="text-zinc-500 text-sm mt-0.5 font-medium">
                            {t('whatsapp_settings_description')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSendReminders}
                        disabled={sendingReminders || saving || loadingPreview || !settings.enabled}
                        className="flex items-center gap-2 px-5 py-2.5 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-all active:scale-95 disabled:opacity-50"
                    >
                        {loadingPreview || sendingReminders ? <Spinner /> : <RiSendPlaneLine size={16} />}
                        {t('send_reminders_now')}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || sendingReminders}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold shadow-lg shadow-red-200 transition-all active:scale-95 disabled:opacity-50"
                    >
                        {saving ? <Spinner /> : <RiSaveLine size={16} />}
                        {t('save')}
                    </button>
                </div>
            </div>

            {/* ── Master Toggle ── */}
            <div className={card}>
                <SectionHeader icon={RiSettings4Line} title={t('general_settings') || 'General Settings'} />
                <Toggle
                    checked={settings.enabled}
                    onChange={v => upd('enabled', v)}
                    label={t('whatsapp_enable') || 'Enable WhatsApp Notifications'}
                    sub={t('whatsapp_enable_sub') || 'Turn all automated WhatsApp messages on or off'}
                />
                <div className="mt-3 flex flex-col md:flex-row gap-6">
                    <div className="flex flex-col gap-1 w-full md:w-1/2" style={{ maxWidth: 300 }}>
                        <label className={labelCls}>{t('default_country_code') || 'Default Country Code'}</label>
                        <div dir="ltr">
                            <PhoneInput
                                country={defaultCountry}
                                value={settings.country_code || ''}
                                onChange={(value, country) => upd('country_code', '+' + country.dialCode)}
                                enableSearch={true}
                                disableSearchIcon={true}
                                buttonStyle={{
                                    backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                                    borderColor: isDarkMode ? '#3f3f46' : '#d1d5db',
                                }}
                                inputStyle={{
                                    width: '100%',
                                    backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                                    borderColor: isDarkMode ? '#3f3f46' : '#d1d5db',
                                    color: isDarkMode ? '#fff' : '#111827',
                                }}
                                dropdownStyle={{
                                    backgroundColor: isDarkMode ? '#27272a' : '#fff',
                                    color: isDarkMode ? '#fff' : '#111827',
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-1 w-full md:w-1/2" style={{ maxWidth: 300 }}>
                        <label className={labelCls}>{t('default_messaging_language') || 'Default Messaging Language'}</label>
                        <select
                            className={inputCls}
                            value={settings.default_language || 'en'}
                            onChange={e => upd('default_language', e.target.value)}
                        >
                            <option value="en">🇺🇸 {t('english') || 'English'}</option>
                            <option value="ar">🇮🇶 {t('arabic') || 'Arabic'}</option>
                            <option value="ku">🇹🇯 {t('kurdish') || 'Kurdish'}</option>
                        </select>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                            {t('default_language_help') || 'Used if the tenant does not have a preferred language set.'}
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Tenant Rent Reminder ── */}
            <div className={card}>
                <SectionHeader icon={RiUserFollowLine} title={t('tenant_rent_reminder') || 'Tenant Rent Reminder'} />
                <Toggle
                    checked={!!settings.tenant_rent_reminder_enabled}
                    onChange={v => upd('tenant_rent_reminder_enabled', v)}
                    label={t('enable_tenant_rent_reminders', 'Enable Reminders')}
                    sub={t('tenant_rent_reminder_sub') || 'Send reminder to tenants before or after payment due date'}
                />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                    <div>
                        <label className={labelCls}>{t('days_before_due') || 'Days Before Due'}</label>
                        <input
                            type="number" min="0" max="30"
                            className={inputCls}
                            value={settings.tenant_rent_reminder_days_before || 3}
                            onChange={e => upd('tenant_rent_reminder_days_before', parseInt(e.target.value))}
                        />
                    </div>
                </div>
            </div>

            {/* ── Service Fee Reminder ── */}
            <div className={card}>
                <SectionHeader icon={RiNotificationLine} title={t('service_fee_reminder') || 'Service Fee Reminder'} />
                <Toggle
                    checked={!!settings.tenant_service_reminder_enabled}
                    onChange={v => upd('tenant_service_reminder_enabled', v)}
                    label={t('enable_service_fee_reminder') || 'Enable Service Fee Reminder'}
                    sub={t('service_fee_reminder_sub') || 'Notify tenants about upcoming service fee payments'}
                />
                <div style={{ maxWidth: 180 }} className="mt-2">
                    <label className={labelCls}>{t('days_before_due') || 'Days Before Due'}</label>
                    <input
                        type="number" min="0" max="30"
                        className={inputCls}
                        value={settings.tenant_service_reminder_days_before || 3}
                        onChange={e => upd('tenant_service_reminder_days_before', parseInt(e.target.value))}
                    />
                </div>
            </div>

            {/* ── Owner Reminder ── */}
            <div className={card}>
                <SectionHeader icon={RiBuildingLine} title={t('owner_reminder') || 'Owner Reminder'} />
                <Toggle
                    checked={!!settings.owner_reminder_enabled}
                    onChange={v => upd('owner_reminder_enabled', v)}
                    label={t('enable_owner_reminders') || 'Enable Owner Reminders'}
                    sub={t('owner_reminder_sub') || 'Notify owners when their tenants have overdue payments'}
                />
            </div>

            {/* ── Payment Confirmation ── */}
            <div className={card}>
                <SectionHeader icon={RiBankCard2Line} title={t('payment_confirmation') || 'Payment Confirmation'} />
                <Toggle
                    checked={!!settings.send_payment_confirmation}
                    onChange={v => upd('send_payment_confirmation', v)}
                    label={t('send_on_payment') || 'Send confirmation when payment is recorded'}
                    sub={t('payment_confirmation_sub') || 'Automatically sent to tenant when a payment is logged'}
                />
            </div>

            {/* Footer Save */}
            <div className="flex justify-end pt-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-8 py-3 rounded-2xl bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-bold shadow-xl shadow-zinc-200 transition-all active:scale-95 disabled:opacity-50"
                >
                    {saving ? <Spinner /> : <RiSaveLine size={18} />}
                    {t('save')}
                </button>
            </div>

            {/* Confirm Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-zinc-900 border border-zinc-800' : 'bg-white'}`}>
                        <div className={`p-6 border-b ${isDarkMode ? 'border-zinc-800' : 'border-gray-100'}`}>
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {t('preview_reminders') || 'Preview Automated Reminders'}
                            </h3>
                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                {previewList.length > 0
                                    ? `Review the list of ${previewList.length} message(s) that will be sent.`
                                    : 'There are no pending reminders to send right now.'}
                            </p>
                        </div>

                        <div className="p-6 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                            {previewList.length === 0 ? (
                                <div className={`py-8 text-center text-sm ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                                    No recipients found based on your current settings and active data.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {previewList.map((item, idx) => (
                                        <div key={idx} className={`p-4 rounded-xl border flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700/50' : 'bg-gray-50 border-gray-100'}`}>
                                            <div>
                                                <div className={`font-semibold text-sm ${isDarkMode ? 'text-zinc-200' : 'text-gray-800'}`}>
                                                    {item.to} <span className="text-xs font-normal opacity-70">({item.phone})</span>
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                    {item.type} • Apt {item.apartment} • Due: {item.dueDate}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    ${parseFloat(item.amount || 0).toLocaleString()}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className={`p-6 border-t ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-gray-100 bg-white'} flex gap-3 justify-end items-center`}>
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                {t('cancel') || 'Cancel'}
                            </button>
                            {previewList.length > 0 && (
                                <button
                                    onClick={executeSendReminders}
                                    disabled={sendingReminders}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
                                >
                                    {sendingReminders ? <Spinner /> : <RiSendPlaneLine size={16} />}
                                    {t('confirm') || 'Confirm & Send All'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
